// !!! KONTOMATIK DOKUMENTACJA: https://developer.kontomatik.com/docs/

import { useEffect, useRef } from "react";
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { BmVerifyModel } from "../BankVerificationPageProps";
import { KontomatikService } from "service/KontomatikService/KontomatikService";
import { AppLinks } from 'enviroment/AppLinks'
import { SessionStorage } from 'common/CookieStorage'
import { STORAGEKEYS } from 'enviroment/Keys'
import { AppState } from 'store'
import { CollectPersonalIdFieldsNames } from 'FormDefinitions/CollectPersonalIdForm/CollectPersonalIdFields'
import { isTrastiBroker } from 'common/helpers/helpers'
import { RequestKontomatikSetSessionProps } from 'service/KontomatikService/KontomatikServiceProps'
import analyticsService from 'service/AnalyticsService/AnalyticsService'


/**
 * Nadpisanie globalnego interface'u window na potrzeby Kontomatika oraz skryptu BIK CFD 
*/
declare global {
  interface Window {
    embedKontomatik: (a: any) => void;
    removeKontomatik: () => void;
  }
}

/**
 * Hook obsługujący werfyikację klienta w banku za pomocą widgetu KONTOMATIKA
 */
const useBankVerificationPageLogic = (): BmVerifyModel  => {
  const sessionStorage = new SessionStorage()
  const navigate = useNavigate()

  const kontomatikService = new KontomatikService()
  const HTML_KONTOMATIK_DIV_ID = 'kontomatikSigninWidgetDivId'
  const isKontomatikWorking = Boolean(!!window.embedKontomatik)
  
  const applicationState = useSelector((state: AppState) => state.applicationState)
  const HAS_KONTOMATIK_CLIENT = applicationState.kontomatikWidgetClient !== ""
  const refKontomatikBankCallback = useRef(() => { })
  const refKontomatikSuccessCallback = useRef((target: string, sessionId: string, sessionIdSignature: string) => { })
  const currentFormState = useSelector((state: AppState) => state.formState)
  const ANALYTICS_EVENT_TYPES_NAMES = useSelector((state: AppState) => state.globalConfigurationState.analyticsEventTypesNames)

  useEffect(() => {
    refKontomatikBankCallback.current = () => { sendKontomatikBank() }

    refKontomatikSuccessCallback.current = (target: string, sessionId: string, sessionIdSignature: string) => {
      sendKontomatikSession(target, sessionId, sessionIdSignature)
    }

  }, [refKontomatikBankCallback, refKontomatikSuccessCallback])


  useEffect(() => {
   if (HAS_KONTOMATIK_CLIENT && isKontomatikWorking && document.getElementById(HTML_KONTOMATIK_DIV_ID)) {
      window.embedKontomatik({
        client: applicationState.kontomatikWidgetClient,  // replace it with your assigned client id
        divId: HTML_KONTOMATIK_DIV_ID,       // must match the div element id
        ownerExternalId: applicationState.kontomatikWidgetOwnerExternalId,
        country: "pl",
        locale: "pl",
        showFavicons: true,

        onConsentsGiven: () => {
          refKontomatikBankCallback.current()
        },

        onSuccess: async(target: string, sessionId: string, sessionIdSignature: string, options: { officialName: string }) => {
          // End-user successfully signed in to the bank
          // Pass target, sessionId and sessionIdSignature to your backend

          await analyticsService.createSingleAnalyticEvent(ANALYTICS_EVENT_TYPES_NAMES?.KontomatikSuccess, JSON.stringify({ 
            sessionId,
            sessionIdSignature,
            bankOfficialName: options.officialName
          }, undefined, 3))

          refKontomatikSuccessCallback.current(target, sessionId, sessionIdSignature);
        },
        onError: async(errorMessage: string, options: { officialName: string, sessionId: string, widgetSessionId: string }) => {
          await analyticsService.createSingleAnalyticEvent(ANALYTICS_EVENT_TYPES_NAMES?.KontomatikError, JSON.stringify({ 
            errorMessage,
            bankOfficialName: options.officialName,
            sessionId: options.sessionId,
            widgetSessionId: options.widgetSessionId,
          }, undefined, 3))
        },
        onTargetSelected: async (bankNamesObject: { name: string, officialName: string }) => {
          const bankOfficialName = bankNamesObject.officialName
          sessionStorage.add(STORAGEKEYS.CURRENT_SELECTED_BANK_NAME,bankOfficialName)

          await analyticsService.createSingleAnalyticEvent(ANALYTICS_EVENT_TYPES_NAMES?.KontomatikBankSelected, JSON.stringify({ bankOfficialName }, undefined, 3))
        },
        onSessionCreated: async (kontomatikSession: { sessionId: string }) => { //Zainicjalizowanie sesji w Kontomatik
          const bankOfficialName = sessionStorage.get(STORAGEKEYS.CURRENT_SELECTED_BANK_NAME)
          const bankSessionCreatedEventData = {...kontomatikSession, bankOfficialName}

          await analyticsService.createSingleAnalyticEvent(ANALYTICS_EVENT_TYPES_NAMES?.KontomatikSessionCreated, JSON.stringify(bankSessionCreatedEventData, undefined, 3))
        },
        onRedirect: async() => {
          const bankOfficialName = sessionStorage.get(STORAGEKEYS.CURRENT_SELECTED_BANK_NAME)     
          await analyticsService.createSingleAnalyticEvent(ANALYTICS_EVENT_TYPES_NAMES?.KontomatikExternalTabBankRedirect, JSON.stringify({ bankOfficialName }, undefined, 3))
        }
      })

     return () => {
       window.removeKontomatik() //cleanup
     }
   }
 }, [HAS_KONTOMATIK_CLIENT, isKontomatikWorking, applicationState.kontomatikWidgetOwnerExternalId])

  const sendKontomatikSession = (target: string, sessionId: string, sessionIdSignature: string): void => {
    const sessionStorage = new SessionStorage()
    let  kontomatikSessionData: RequestKontomatikSetSessionProps = {
      sessionId: sessionId,
      sessionIdSignature: sessionIdSignature,
      bankName: target,
      hdim: sessionStorage.get(STORAGEKEYS.JSC) ?? '',
      jsc:  sessionStorage.get(STORAGEKEYS.HDIM) ?? '',
    }

    if(isTrastiBroker()) {
      kontomatikSessionData = {
        ...kontomatikSessionData, 
        personalId: currentFormState[CollectPersonalIdFieldsNames.PersonalId].value as string, 
        personalIdRelease: currentFormState[CollectPersonalIdFieldsNames.PersonalIdRelease].value as string,
      }
    }

    kontomatikService
      .setSessionData({...kontomatikSessionData})
      .then((p) => {
        sessionStorage.add(STORAGEKEYS.IS_AFTER_BANK_VERIFICATION , "true")
        navigate(AppLinks.bmWaiting)
      })
      .catch(err => { 
        navigate(AppLinks.error)
      })
  }

  const sendKontomatikBank = () => (): void => {
    kontomatikService.setBankChosen({})
      .then((p) => { /* akcja na wybór banku */ })
      .catch(err => { /* akcja gdy wybór banku się nie powiedzie */ })
  }

  return {
    isKontomatikWorking,
    HTML_KONTOMATIK_DIV_ID,
    isAdditionalVerificationMethodsAvailable: applicationState.isAdditionalVerificationMethodsAvailable,
  }
}

export default useBankVerificationPageLogic;